/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef } from "react";
import {
  Grid,
  Paper,
  Button,
  Tooltip,
  Typography,
  Chip,
  Box,
} from "@mui/material";
import MyTable, { TableRow } from "../../../components/MyTable";
import { TableHeadRows } from "../../../components/MyTable/TableHead";
import MySnackbar from "../../../components/MySnackbar";
import DownloadIcon from "@mui/icons-material/Download";
import {
  DownloadResponse,
  getPaymentSlipsDownloadMonitor,
  PaymentSlipDownload,
  paymentSlipsBulkDownload,
} from "../../../api/s10DocumentsApi";

// Definición de estados con descripciones para tooltips
const STATUS_INFO = {
  Registrado: "Solicitud registrada, pendiente de procesamiento.",
  "En Proceso": "El sistema está generando el archivo.",
  Completado: "Archivo generado exitosamente.",
  Error: "Ocurrió un error durante el procesamiento.",
  "Sin Datos": "No se encontró información para generar el archivo.",
};

const AdminPaymentSlipDownloads = () => {
  const [data, setData] = useState<PaymentSlipDownload[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>("");

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchDownloads = async () => {
    setLoading(true);
    try {
      const result = await getPaymentSlipsDownloadMonitor();
      console.log("getPaymentSlipsDownloadMonitor", result);
      setData(result || []);
      setTotalItems(result?.length || 0);
    } catch (error: any) {
      setErrorMsg(error.message || "Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (id: number) => {
    try {
      const result: DownloadResponse = await paymentSlipsBulkDownload(id);
      if (result.Type !== 0) {
        throw new Error(result.Message || "Error al obtener la URL de descarga");
      }
      const { Presigned_URL } = result.Value;
      window.open(Presigned_URL, "_blank");
    } catch (error: any) {
      setErrorMsg(error.message || "Error al descargar el archivo");
    }
  };

  const DownloadButton = (headRow: TableHeadRows, row: TableRow<string, any>) => {
    if (row.Status !== "Completado") {
      return <></>;
    }

    const creationDate = new Date(row.CreationDate);
    const now = new Date();
    const diffDays = (now.getTime() - creationDate.getTime()) / (1000 * 3600 * 24);
    const isExpired = diffDays >= 2;

    const handleClick = () => {
      if (!isExpired) {
        handleDownload(row.Id);
      }
    };

    const button = (
      <Button
        variant="contained"
        color={isExpired ? "inherit" : "primary"}
        size="small"
        startIcon={<DownloadIcon />}
        onClick={handleClick}
        disabled={isExpired}
        sx={{ minWidth: "100px" }}
      >
        {isExpired ? "Expirado" : "Descargar"}
      </Button>
    );

    if (isExpired) {
      return (
        <Tooltip title="El enlace de descarga ha expirado. Por seguridad, solo está disponible durante 2 días a partir de la generación del reporte.">
          <span>{button}</span>
        </Tooltip>
      );
    }

    return button;
  };

  const tableHeadRows: TableHeadRows[] = [
    {
      label: "RUC",
      rowKeyName: "RUC",
      allowOrderAction: true,
    },
    {
      label: "Nombre RUC",
      rowKeyName: "RUCName",
      allowOrderAction: true,
    },
    {
      label: "Esquema",
      rowKeyName: "SchemePayrollName",
      allowOrderAction: true,
    },
    {
      label: "Fecha Inicio",
      rowKeyName: "DateStart",
      allowOrderAction: true,
    },
    {
      label: "Fecha Fin",
      rowKeyName: "DateEnd",
      allowOrderAction: true,
    },
    {
      label: "Usuario",
      rowKeyName: "CreationUser",
      allowOrderAction: true,
    },
    {
      label: "Estado",
      rowKeyName: "Status",
      allowOrderAction: true,
    },
    {
      label: "Fecha Creación",
      rowKeyName: "CreationDate",
      allowOrderAction: true,
    },
    {
      label: "Acción",
      rowKeyName: "action",
      customValue: DownloadButton,
    },
  ];

  useEffect(() => {
    fetchDownloads();

    intervalRef.current = setInterval(() => {
      fetchDownloads();
    }, 60000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  return (
    <Grid item width="100%" marginTop="1.5rem" minHeight="100%">
      <Paper
        elevation={3}
        style={{
          width: "100%",
          borderRadius: "0.5rem",
          minHeight: "10rem",
          padding: "2rem 1.5rem",
        }}
      >
        {/* Nota de expiración (más concisa) y leyenda de estados con tooltips */}
        <Box>

          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              <strong>Estados:</strong>
            </Typography>
            {Object.entries(STATUS_INFO).map(([status, description]) => (
              <Tooltip key={status} title={description} arrow>
                <Chip
                  size="small"
                  label={status}
                  color={
                    status === "Registrado"
                      ? "default"
                      : status === "En Proceso"
                      ? "warning"
                      : status === "Completado"
                      ? "success"
                      : status === "Error"
                      ? "error"
                      : "info"
                  }
                  variant="outlined"
                />
              </Tooltip>
            ))}
          </Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Nota:</strong> Los enlaces de descarga expiran a los 2 días de generados por seguridad.
          </Typography>
        </Box>

        <MyTable
          key="tab-downloads"
          pageNumber={page}
          itemsPerPage={pageSize}
          totalItems={totalItems}
          totalPages={Math.ceil(totalItems / pageSize)}
          onChangePageNumber={setPage}
          onChangeItemsPerPage={setPageSize}
          hiddenSearchInput={true}
          tableHeadRows={tableHeadRows}
          tableRows={data}
          loading={loading}
          tableOptionsBar={{
            useDrawer: false,
          }}
          hiddenFilterOptions={true}
        />
      </Paper>

      {errorMsg && (
        <MySnackbar
          msg={errorMsg}
          severity="error"
          open={!!errorMsg}
          onClose={() => setErrorMsg("")}
        />
      )}
    </Grid>
  );
};

export default AdminPaymentSlipDownloads;